import React from "react";
import Reveal from "./Reveal";
import { ArrowRight } from "lucide-react";
import { Compass, Layers, Wrench, ArrowUpRight } from "lucide-react";
import { trackEvent } from "../../lib/track";

/**
 * Self-Identification Section.
 *
 * Replaces the previous WhereDoYouFit + ThisIsForYouIf pair with a
 * single tighter section. The visitor:
 *   1. Sees three functional paths and picks the closest one.
 *   2. Reads the "for you / not for you" qualifier strip so they
 *      can self-locate AND self-exit cleanly.
 *
 * The 4 archetype cards (Founders / Builders / Creatives /
 * Entrepreneurs) that used to live in ThisIsForYouIf were
 * intentionally MOVED to /start-here, not removed - they belong
 * inside the deeper audience-identification work that visitors
 * who reach Start Here are ready for.
 */

const PATHS = [
  {
    id: "personal-clarity",
    Icon: Compass,
    label: "Personal Clarity",
    body:
      "For people in transition, reinvention, burnout, identity shifts, or self-trust rebuilding who need language, direction, and a grounded next step.",
    next:
      "A next step that often helps: the free Clarity Snapshot, or the book if you want the full philosophy first.",
    cta: { label: "See This Path", href: "/start-here#personal-clarity" },
  },
  {
    id: "brand-business",
    Icon: Layers,
    label: "Brand & Business Structure",
    body:
      "For creators, founders, consultants, healers, and service providers with ideas, gifts, or offers that need clearer positioning, messaging, and structure.",
    next:
      "A next step that often helps: the Personal Action Plan, or a Clarity Reset Session for guided help.",
    cta: { label: "See This Path", href: "/start-here#brand-business" },
  },
  {
    id: "systems-execution",
    Icon: Wrench,
    label: "Systems & Execution",
    body:
      "For people who need help turning ideas into funnels, AI workflows, content systems, offers, implementation plans, or operational structure.",
    next:
      "A next step that often helps: a Clarity Reset Session, then a Structured Momentum Sprint when the build is clear.",
    cta: { label: "See This Path", href: "/start-here#systems-execution" },
  },
];

const FOR_YOU = [
  "You have ideas, but no clean structure.",
  "You know you are meant for more, but do not know what to build first.",
  "You are tired of carrying potential without a clear pathway.",
  "You need language for what you do.",
  "You need help turning your gift into an offer, system, brand, or next move.",
  "You have relationships and opportunities, but no activation strategy.",
  "You are ready for clarity, but not more empty motivation.",
  "You want grounded movement, not just inspiration.",
];

const NOT_FOR_YOU = [
  "You only want inspiration with no action.",
  "You are not willing to participate in the process.",
  "You want someone else to carry the entire vision for you.",
  "You are not ready to make decisions or move with structure.",
];

const SelfIdentification = () => (
  <section
    id="self-identification"
    data-testid="self-identification-section"
    className="relative py-24 md:py-28 border-t border-gold/10"
  >
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <Reveal>
        <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">
          Where You Fit
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-themed leading-tight tracking-tight font-light text-center max-w-3xl mx-auto">
          Where are you trying to{" "}
          <span className="italic gold-gradient-text">create momentum?</span>
        </h2>
        <p className="mt-7 max-w-2xl mx-auto text-center text-white/85 leading-relaxed">
          Authentic Momentum meets you at the point where potential, pressure, vision, and uncertainty collide. Pick the path that feels closest. Read the strip beneath to confirm this is for you.
        </p>
        <p
          data-testid="self-identification-season-line"
          className="mt-4 max-w-2xl mx-auto text-center text-gold/85 text-[13.5px] tracking-[0.08em]"
        >
          Choose the one that sounds most like the season you are in right now.
        </p>
      </Reveal>

      <div
        data-testid="self-identification-grid"
        className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6"
      >
        {PATHS.map((p, i) => (
          <Reveal key={p.id} delay={120 + i * 90}>
            <a
              href={p.cta.href}
              data-testid={`self-id-path-${p.id}`}
              onClick={() => trackEvent("self_identification_path_clicked", { path: p.id })}
              className="group block bg-white/5 border-t border-gold/20 p-6 md:p-7 rounded-lg border border-gold/10 h-full hover:border-gold/30 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center text-gold group-hover:border-gold/70 transition-colors">
                  <p.Icon size={17} strokeWidth={1.4} />
                </div>
                <ArrowUpRight size={16} className="text-gold/50 group-hover:text-gold transition-colors" />
              </div>
              <h3 className="mt-5 font-serif text-xl md:text-[1.55rem] text-themed font-light">
                {p.label}
              </h3>
              <p className="mt-3 text-white/85 text-[15px] leading-relaxed">
                {p.body}
              </p>
              <p
                data-testid={`self-id-next-${p.id}`}
                className="mt-3 text-white/65 text-[13.5px] leading-relaxed italic"
              >
                {p.next}
              </p>
              <p className="mt-6 text-[11px] tracking-[0.22em] uppercase text-gold group-hover:text-[#F3E5AB] transition-colors">
                {p.cta.label} <ArrowRight size={11} className="inline ml-1" />
              </p>
            </a>
          </Reveal>
        ))}
      </div>

      <div
        data-testid="self-identification-qualifier"
        className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6"
      >
        <Reveal delay={120}>
          <div className="bg-white/5 border-t border-gold/20 p-6 md:p-7 rounded-lg border border-gold/10 h-full">
            <p className="text-[10px] tracking-[0.4em] uppercase text-gold mb-4">
              This is for you if
            </p>
            <ul data-testid="for-you-list" className="space-y-2.5">
              {FOR_YOU.map((line, i) => (
                <li
                  key={i}
                  className="flex gap-3 text-white/85 text-[14.5px] leading-relaxed"
                >
                  <span className="text-gold/80 shrink-0">•</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={200}>
          <div className="bg-white/[0.03] border-t border-gold/10 p-6 md:p-7 rounded-lg border border-gold/[0.08] h-full">
            <p className="text-[10px] tracking-[0.4em] uppercase text-gold/65 mb-4">
              This may not be for you if
            </p>
            <ul data-testid="not-for-you-list" className="space-y-2.5">
              {NOT_FOR_YOU.map((line, i) => (
                <li
                  key={i}
                  className="flex gap-3 text-white/65 text-[14.5px] leading-relaxed"
                >
                  <span className="text-gold/40 shrink-0">•</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default SelfIdentification;
